import { computed, ref, watch } from "vue";

import { useNgSession } from "@sazka/ng/ng-session";
import { UrlUtils } from "@sazka/utils/url";
import { sazkaAlert } from "@sazka/utils/sazka-alert";
import { Loader } from "@sazka/design-system/src/loader/loader";

import {
    fetchPlayerBoard,
    fetchPlayerDraw,
    fetchPlayerAllwynDraw,
    fetchTotalTickets,
    fetchQuests,
    fetchQuestDetail,
    postActivateQuest,
    postDeactivateQuest,
    completePlayerBoard,
    fetchQuestGames,
} from "../api/bonusomat-player-data-api";

import { BonusomatBoard } from "../models/bonusomat-board";
import { BonusomatAllwynDraw, BonusomatDefaultDraw } from "../models/bonusomat-draw";
import { BonusomatQuest } from "../models/bonusomat-quest";
import { BonusomatQuestDetail } from "../models/bonusomat-quest-detail";
import { BonusomatQuestGame } from "../models/bonusomat-quest-detail";
import { BonusomatQuestGameType } from "../models/bonusomat-quest-game-type";
import { BonusomatDatalayer } from "../utils/analytics/bonusomat-datalayer";
import { useLoyaltyPlayerStatus } from "@/loyalty/composables/use-loyalty-player-status";
import { logException } from "@sazka/utils/logger/use-app-logger";
import { isMobileApp, triggerMobileAppEvent } from "@sazka/mobile-apps/mobile-app-integration";
import { loadCasinoModule } from "@sazka/casino/js/core/use-ng-casino";

// PLAYER ID
const { session, isLoggedIn } = useNgSession();
const playerId = computed(() => session.value?.PlayerID);

/* ******************************* PLAYER DATA **************************** */

const { bonusomatPlayerStatus } = useLoyaltyPlayerStatus("Bonusomat");
const { bonusomatPlayerData, remainingSpinsCount, loadBonusomatPlayerData } = bonusomatPlayerStatus;

/* ******************************* BOARD **************************** */
//#region BOARD

// BOARD variables
const boardData = ref<BonusomatBoard>(null);
const isBoardDataLoaded = ref(false);

// BOARD metods
async function loadBoardData() {
    try {
        boardData.value = await fetchPlayerBoard(playerId.value);
    } finally {
        isBoardDataLoaded.value = true;
    }
}

async function completeBoard() {
    Loader.show();

    try {
        await completePlayerBoard(playerId.value);

        await loadBonusomatPlayerData();
        await loadBoardData();
        await loadDefaultDrawData();
        await loadAllwynDrawData();
    } catch (error) {
        sazkaAlert("Omlouváme se, při převzetí tiketu nastala chyba.", "Zkuste to prosím později.");
        logException("bonusomat-board", error, { message: "Complete board error" });
    } finally {
        Loader.hide();
    }
}
//#endregion

/* **************************** RAFFLE ********************** */
//#region RAFFLE

const defaultDrawData = ref<BonusomatDefaultDraw>(null);
const allwynDrawData = ref<BonusomatAllwynDraw>(null);
const isDefaultDrawDataLoaded = ref(false);
const isAllwynDrawDataLoaded = ref(false);

async function loadDefaultDrawData() {
    if (!playerId.value) {
        defaultDrawData.value = null;
        return;
    }

    try {
        defaultDrawData.value = await fetchPlayerDraw();

        if (defaultDrawData.value) {
            const endDate = new Date(defaultDrawData.value.endDate);
            const now = new Date();

            defaultDrawData.value.drawDate = new Date(defaultDrawData.value.drawDate);
            defaultDrawData.value.endDate = endDate;

            setTimeout(() => {
                defaultDrawData.value.drawRemainingSeconds = Math.floor((endDate.getTime() - now.getTime()) / 1000);
            }, 1500);
        }
    } catch {
        defaultDrawData.value = null;
    } finally {
        isDefaultDrawDataLoaded.value = true;
    }
}

async function loadAllwynDrawData() {
    if (!playerId.value) {
        allwynDrawData.value = null;
        return;
    }

    try {
        allwynDrawData.value = await fetchPlayerAllwynDraw();

        if (allwynDrawData.value) {
            const endDate = new Date(allwynDrawData.value.endDate);
            const now = new Date();

            allwynDrawData.value.endDate = endDate;

            setTimeout(() => {
                allwynDrawData.value.drawRemainingSeconds = Math.floor((endDate.getTime() - now.getTime()) / 1000);
            }, 1500);
        }
    } catch {
        allwynDrawData.value = null;
    } finally {
        isAllwynDrawDataLoaded.value = true;
    }
}

async function loadTotalTickets() {
    if (!playerId.value) {
        if (defaultDrawData.value) {
            defaultDrawData.value.totalTickets = 0;
        }
        return;
    }

    try {
        defaultDrawData.value.totalTickets = await fetchTotalTickets(playerId.value);
    } catch (ex: unknown) {
        logException("bonusomat-ticket", ex, { message: "Load ticket error" });
    }
}

//#endregion

/* **************************** QUESTS ************************ */
//#region QUESTS

// QUEST variables
const questsData = ref<BonusomatQuest[]>(null);
const lastActivatedQuestId = ref<number>(null);
const openQuestLinkName = ref<string>(null);
const openedGamesTaskIndex = ref<number>(-1);
let deactivationEventSource: string = null;

const isQuestsDataLoaded = ref(false);
const questsDataHasError = ref(false);

const isQuestModalOpen = ref(false);
const isQuestConfirmOpen = ref(false);

// used to reload progress components on certain actions (fixes issue on IOS where progress animation doesn't work reliably by itself)
const questCardProgressLoadCounter = ref(0);
const questDetailProgressLoadCounter = ref(0);

const openedQuest = ref<BonusomatQuestDetail>(null);
// eslint-disable-next-line
let openedQuestNeedsReload = false;

const hasActiveQuest = computed(() => {
    const activeQuestId = bonusomatPlayerData.value.activeQuestId;

    return activeQuestId !== null && activeQuestId !== undefined && activeQuestId >= 0;
});

const activeQuest = computed(() => {
    if (hasActiveQuest.value) {
        const activeQuestId = bonusomatPlayerData.value.activeQuestId;
        return questsData.value?.find((quest) => quest.id === activeQuestId);
    }

    return null;
});

const isActiveQuestCancelable = computed(() => {
    return activeQuest.value?.cancelable ?? true;
});

// QUESTS metods
async function loadQuests() {
    if (!playerId.value) {
        questsData.value = [];
        return;
    }

    try {
        questsData.value = await fetchQuests(playerId.value);
        questsDataHasError.value = false;
    } catch (error) {
        questsDataHasError.value = true;
        logException("bonusomat-quest", error, { message: "Load quests error" });
    } finally {
        isQuestsDataLoaded.value = true;
    }
}

async function openQuestDetail(questId: number) {
    try {
        if (!openedQuestNeedsReload && openedQuest.value?.id === questId) return;

        openedQuest.value = await fetchQuestDetail(playerId.value, questId);
        openedQuestNeedsReload = false;
    } catch (error) {
        sazkaAlert("Omlouváme se, při načítání detailu Questu nastala chyba.", "Zkuste to prosím později.");
        logException("bonusomat-quest", error, { message: "Open quests detail error" });
    } finally {
        // For not-completed quests preselect first task
        openedGamesTaskIndex.value = openedQuest.value?.completed ? -1 : 0;

        if (openedQuest.value) {
            isQuestModalOpen.value = true;
        }
    }
}

async function activateQuest(questId: number, alreadyConfirmed = false) {
    let hasErrorOccurred = false;
    lastActivatedQuestId.value = questId;
    const questToActivate = questsData.value?.find((quest) => quest.id === questId);

    if (hasActiveQuest.value && !alreadyConfirmed) {
        isQuestConfirmOpen.value = true;
    } else {
        try {
            Loader.show();
            await postActivateQuest(playerId.value, questId);
            if (!isActiveQuestCancelable.value) {
                questsData.value?.splice(
                    questsData.value?.findIndex((quest) => quest.id === bonusomatPlayerData.value.activeQuestId),
                    1
                );
            }
        } catch (error) {
            sazkaAlert("Omlouváme se, při aktivaci Questu nastala chyba.", "Zkuste to prosím později.");
            logException("bonusomat-quest", error, { message: "Activate quests error" });
            hasErrorOccurred = true;
        } finally {
            Loader.hide();
            if (!hasErrorOccurred) {
                await loadBonusomatPlayerData();

                // For quest without tasks reload quest data because it should be immediately completed
                if (questToActivate && !questToActivate.totalTasks) {
                    openedQuestNeedsReload = true;
                    await loadQuests();
                    await openQuestDetail(questToActivate.id);
                }
            }
        }
    }
}

async function deactivateQuest(eventSource: string = null, alreadyConfirmed = false) {
    let hasErrorOccurred = false;
    lastActivatedQuestId.value = null;

    if (!alreadyConfirmed) {
        isQuestConfirmOpen.value = true;
        deactivationEventSource = eventSource;
    } else {
        try {
            Loader.show();
            await postDeactivateQuest(playerId.value);
            if (!isActiveQuestCancelable.value) {
                questsData.value?.splice(
                    questsData.value?.findIndex((quest) => quest.id === bonusomatPlayerData.value.activeQuestId),
                    1
                );
            }
        } catch (error) {
            sazkaAlert("Omlouváme se, při deaktivaci Questu nastala chyba.", "Zkuste to prosím později.");
            logException("bonusomat-quest", error, { message: "Deactivate quests error" });
            hasErrorOccurred = true;
        } finally {
            Loader.hide();

            if (deactivationEventSource) {
                BonusomatDatalayer.pushDeactivateQuest(
                    deactivationEventSource,
                    bonusomatPlayerData.value.points,
                    remainingSpinsCount.value
                );
                deactivationEventSource = null;
            }

            if (!hasErrorOccurred) {
                await loadBonusomatPlayerData();
            }
        }
    }
}

const questLinkNameParam = "quest";
const queryParams = new URLSearchParams(window.location.search);
openQuestLinkName.value = queryParams.get(questLinkNameParam);
if (openQuestLinkName.value) {
    UrlUtils.clearUrlParams([questLinkNameParam]);
}

watch([isQuestsDataLoaded, isLoggedIn], async () => {
    if (isQuestsDataLoaded.value && isLoggedIn.value && openQuestLinkName.value) {
        const questWithLinkName = questsData.value?.find(
            (quest) => quest.linkName.toLowerCase() === openQuestLinkName.value.toLowerCase()
        );
        if (questWithLinkName) {
            await openQuestDetail(questWithLinkName.id);
            BonusomatDatalayer.pushOpenQuest(
                questWithLinkName.title,
                "link",
                bonusomatPlayerData.value.points,
                remainingSpinsCount.value
            );
        }
    }
});

watch(isLoggedIn, () => {
    openedQuestNeedsReload = true;
});

//#endregion

/* ******************************* GAMES DATA **************************** */
//#region GAMES

const gamesData = ref<BonusomatQuestGame[]>([]);
const loadGamesData = async () => {
    gamesData.value = await fetchQuestGames();
};

const openGame = async (id: string, type: BonusomatQuestGameType) => {
    let idParsed: string | number = id;

    if (isMobileApp) {
        // UFO-2102 - European Roulette is specific, has different id for desktop and for mobile version (set by editors)
        if (id === "592") idParsed = "1556";

        triggerMobileAppEvent(
            "goToDeepLink",
            `sazkahry://home/${type == BonusomatQuestGameType.LiveDealer ? "gameslive" : "games"}/${idParsed}`
        );
    } else {
        idParsed = parseInt(id);

        const { useNgCasino } = await loadCasinoModule();
        useNgCasino().loadGame({ gameId: idParsed, moneyMode: true });
    }
};

//#endregion

/* ************************** INIT **************************** */
//#region INIT

async function loadBonusomatData(isForWidget = false) {
    if (isForWidget) {
        await loadQuests();
    } else {
        const boardPromise = loadBoardData();
        const questsPromise = loadQuests();
        const defaultDrawDataPromise = loadDefaultDrawData();
        const allwynDrawDataPromise = loadAllwynDrawData();

        await Promise.all([boardPromise, questsPromise, defaultDrawDataPromise, allwynDrawDataPromise]);
    }
}

let hasGameCloseEventInited = false;
async function initBonusomatData(isForWidget = false) {
    await loadBonusomatData(isForWidget);

    if (!hasGameCloseEventInited) {
        ng_hs.instantGame.gameClosed(async () => {
            if (!isLoggedIn.value) return;

            await loadBonusomatData(isForWidget);
            await loadBonusomatPlayerData();
            openedQuestNeedsReload = true;
        });

        hasGameCloseEventInited = true;
    }
}

//#endregion

export default function useBonusomatData() {
    return {
        boardData,
        isBoardDataLoaded,
        initBonusomatData,
        loadBoardData,
        completeBoard,

        defaultDrawData,
        allwynDrawData,
        isDefaultDrawDataLoaded,
        isAllwynDrawDataLoaded,
        loadDefaultDrawData,
        loadAllwynDrawData,
        loadTotalTickets,

        questsData,
        lastActivatedQuestId,
        openQuestLinkName,
        openedGamesTaskIndex,
        isQuestsDataLoaded,
        questsDataHasError,
        isQuestModalOpen,
        isQuestConfirmOpen,
        questCardProgressLoadCounter,
        questDetailProgressLoadCounter,
        openedQuest,
        hasActiveQuest,
        activeQuest,
        isActiveQuestCancelable,
        loadQuests,
        openQuestDetail,
        activateQuest,
        deactivateQuest,

        gamesData,
        loadGamesData,
        openGame,
    };
}
